/**
 * Position, score, progress and restart for the in-lesson quiz — §2.8 #72.
 *
 * Extracted from `LessonQuizRunner`. Purely presentational.
 *
 * The score reads `score/scoredCount`, not `score/total`, and that is
 * deliberate: mid-run the denominator is how many questions have been ANSWERED,
 * so "2/3" means two right out of three attempted rather than two out of a
 * twenty-question quiz. Showing the full total here would make every run look
 * like a failure until the last question.
 */
import { RestartButton } from './RestartButton';

export interface QuizRunnerHeaderProps {
  /** Zero-based index of the question on screen. */
  index: number;
  total: number;
  /** Questions answered correctly so far. */
  score: number;
  /** Questions answered so far — the score's denominator. */
  scoredCount: number;
  onRestart: () => void;
}

/**
 * One line, then a thin bar. It used to be two rows saying the same thing
 * twice - "1/4" with dots, and "Score: 0/1" under it.
 */
export function QuizRunnerHeader({
  index,
  total,
  score,
  scoredCount,
  onRestart,
}: QuizRunnerHeaderProps) {
  const answered = scoredCount;
  return (
    <div className='mb-3'>
      <div className='flex items-center gap-3'>
        <span className='text-sm font-medium text-foreground'>
          {`Question ${index + 1} of ${total}`}
        </span>
        <span className='ml-auto font-mono text-xs text-muted-foreground'>
          {/* brand-700, not 600: 600 measured 4.48:1 at this size on the
              lesson surface — under the 4.5 text gate. */}
          <span className='font-semibold text-brand-700 dark:text-primary'>
            {score}/{scoredCount}
          </span>{' '}
          right
        </span>
        <RestartButton
          what='quiz'
          hasProgress={answered > 0}
          onRestart={onRestart}
        />
      </div>
      <div
        className='mt-2 h-1 overflow-hidden rounded-full bg-muted'
        role='progressbar'
        aria-label='Quiz progress'
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={answered}
      >
        <div
          className='h-full rounded-full bg-brand-500 transition-[width] duration-300 dark:bg-primary'
          style={{ width: `${total > 0 ? (answered / total) * 100 : 0}%` }}
        />
      </div>
    </div>
  );
}
